/**
 * MOVIO V2 — the 14-scene presentation.
 *
 * Each slide is one scene of one continuous film. This file owns the overlay copy and the
 * story timing; the 3D world (src/components/world) owns the camera shots and the scenes.
 * All copy and figures come from src/data/movio.ts, which stays the single source of truth.
 */
import {
  brand,
  expansion,
  ideaStory,
  industryById,
  metrics,
  partnerCta,
  partnerLinks,
  partnershipModel,
  superApp,
  transformation,
  type IndustryId,
} from '@/data/movio';
import { evenStory, type Story } from './story';

export type StationKey =
  | 'hero'
  | 'business'
  | 'technology'
  | 'mobility'
  | 'logistics'
  | 'ai-voice'
  | 'services'
  | 'agri'
  | 'future'
  | 'transformation'
  | 'ecosystem'
  | 'super-app'
  | 'growth'
  | 'partner';

export interface SlideCta {
  label: string;
  href: string;
  variant?: 'primary' | 'ghost';
}

export interface SlideDef {
  /** DOM id and URL hash (kept compatible with existing /homepage#… links). */
  id: string;
  key: StationKey;
  /** Short name used by the slide indicator. */
  label: string;
  eyebrow: string;
  headline: string[];
  /** Short line shown directly under the headline. */
  tagline?: string;
  body?: string;
  ctas?: SlideCta[];
  /** Story steps shown in the overlay and played by the 3D scene. */
  steps?: string[];
  story?: Story;
  chips?: string[];
  note?: string;
  /** Approximate camera position, used to scale transition duration by travel distance. */
  anchor: [number, number, number];
}

const industrySlide = (
  id: IndustryId,
  anchor: SlideDef['anchor'],
  story: Story,
  key: StationKey = id as StationKey
): SlideDef => {
  const industry = industryById[id];
  return {
    id: `industry-${id}`,
    key,
    label: industry.short,
    eyebrow: `${industry.index} · ${industry.title}`,
    headline: [industry.headline],
    body: industry.description,
    steps: industry.steps,
    story,
    ctas: [{ label: industry.cta, href: industry.href, variant: 'ghost' }],
    note: industry.note,
    anchor,
  };
};

export const slides: SlideDef[] = [
  {
    id: 'top',
    key: 'hero',
    label: 'MOVIO',
    eyebrow: 'Technology partner for established businesses',
    headline: brand.headline,
    tagline: 'Your Technology Partner',
    body: brand.support,
    ctas: [
      { label: partnerLinks.become.label, href: partnerLinks.become.href },
      { label: partnerLinks.explore.label, href: '#movio-idea', variant: 'ghost' },
    ],
    anchor: [-18, 30, 62],
  },
  {
    id: 'movio-idea',
    key: 'business',
    label: 'The Business',
    eyebrow: 'You bring the business',
    headline: ['You Know the Business.'],
    body: 'Your customers, your fleet, your team and years of local trust. The hardest part of the business already exists.',
    chips: partnershipModel[0].points,
    story: evenStory(3, 2.6, 4),
    anchor: [-24, 3.5, 22],
  },
  {
    id: 'technology',
    key: 'technology',
    label: 'The Technology',
    eyebrow: 'MOVIO brings the technology',
    headline: ['We Build the Technology.'],
    body: ideaStory.body,
    steps: ['Digital Layer', 'Apps', 'Booking', 'Dispatch', 'Payments', 'AI', 'Analytics'],
    story: { durations: [2.2, 1.3, 1.3, 1.3, 1.3, 1.3, 1.6], hold: 3.5 },
    anchor: [-22, 10, 22],
  },
  industrySlide('mobility', [8, 12, 26], {
    durations: [1.8, 1.5, 1.5, 3.4, 1.6, 3.8, 2],
    hold: 2.6,
  }),
  industrySlide('logistics', [38, 8, 10], { durations: [2.2, 1.8, 3.6, 2.2, 2], hold: 2.6 }),
  industrySlide('ai-voice', [33, 17, 6], {
    durations: [2.4, 1.6, 1.8, 1.6, 1.8, 2],
    hold: 2.8,
  }),
  industrySlide('services', [62, 8, 28], {
    durations: [1.6, 1.4, 1.8, 1.6, 3.6, 2, 1.8],
    hold: 2.6,
  }),
  industrySlide('agri', [92, 7, 30], { durations: [1.8, 1.6, 1.8, 3.6, 3.4, 1.8], hold: 2.6 }),
  industrySlide('future', [78, 12, 16], { durations: [2.4, 2.4, 2.2, 5], hold: 3 }),
  {
    id: 'transformation',
    key: 'transformation',
    label: 'Transformation',
    eyebrow: transformation.eyebrow,
    headline: [transformation.headline],
    body: 'Phone calls, paper registers and manual dispatch dissolve into one connected technology platform.',
    steps: ['Manual operations', 'Dissolve', 'Rebuild', 'Connected business'],
    story: { durations: [2.8, 1.8, 2.6, 2.4], hold: 3.5 },
    chips: transformation.after,
    anchor: [-31, 2, 17],
  },
  {
    id: 'ecosystem',
    key: 'ecosystem',
    label: 'Ecosystem',
    eyebrow: 'MOVIO Ecosystem',
    headline: ['One Technology Foundation.', 'Multiple Businesses.'],
    body: 'Every MOVIO business runs on the same core: apps, dispatch, payments, AI and data, shared and improved across the network.',
    anchor: [30, 70, 110],
  },
  {
    id: 'super-app',
    key: 'super-app',
    label: 'Super App',
    eyebrow: superApp.eyebrow,
    headline: [superApp.headline],
    body: superApp.body,
    steps: superApp.flow,
    story: evenStory(superApp.flow.length, 1.5, 3),
    chips: superApp.services,
    anchor: [30, 21, 34],
  },
  {
    id: 'success-stories',
    key: 'growth',
    label: 'Growth',
    eyebrow: 'Growth · Success Stories',
    headline: expansion.headline,
    body: 'Established operators in Tamil Nadu already run on MOVIO. The network grows with every partner that joins.',
    story: evenStory(4, 1.6, 6),
    note: expansion.disclaimer,
    anchor: [30, 320, 260],
  },
  {
    id: 'partner',
    key: 'partner',
    label: 'Partner',
    eyebrow: 'Become a MOVIO Partner',
    headline: partnerCta.headline,
    body: partnerCta.body,
    ctas: [
      { label: partnerLinks.become.label, href: partnerLinks.become.href },
      { label: partnerLinks.tellUs.label, href: partnerLinks.tellUs.href, variant: 'ghost' },
    ],
    anchor: [-29, 1.5, 17],
  },
];

export const SLIDE_COUNT = slides.length;

export const slideIndexById = Object.fromEntries(slides.map((s, i) => [s.id, i])) as Record<
  string,
  number
>;

export const slideIndexByKey = Object.fromEntries(slides.map((s, i) => [s.key, i])) as Record<
  StationKey,
  number
>;

export { metrics };

/**
 * Transition length in seconds. Longer camera journeys take longer, capped so the
 * presentation never feels sluggish. Reduced motion uses a short veiled cut.
 */
export function transitionSeconds(
  from: number,
  to: number,
  { reducedMotion = false, compact = false } = {}
) {
  if (reducedMotion) return 0.5;
  const a = slides[from]?.anchor ?? [0, 0, 0];
  const b = slides[to]?.anchor ?? [0, 0, 0];
  const dist = Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
  const seconds = Math.min(3, Math.max(1.7, 1.55 + dist / 150));
  return compact ? seconds * 0.82 : seconds;
}
